import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  UserCog,
  Plus,
  Search,
  Phone,
  Mail,
  Calendar,
  Shield,
  CheckCircle,
  AlertCircle,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Staff() {
  const { organization } = useAuth();
  const [staff, setStaff] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newStaff, setNewStaff] = useState({
    user_id: '',
    employee_id: '',
    job_title: '',
    department: '',
    start_date: '',
    contract_type: 'permanent',
    hourly_rate: '',
    dbs_check_date: '',
    dbs_certificate_number: '',
    right_to_work_verified: false,
    right_to_work_expiry: '',
    visa_type: '',
  });

  useEffect(() => {
    fetchData();
  }, [organization]);

  const fetchData = async () => {
    try {
      const [staffRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/staff`, { params: { organization_id: organization?.id } }),
        axios.get(`${API_URL}/api/users`, { params: { organization_id: organization?.id } })
      ]);
      setStaff(staffRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/staff`, {
        ...newStaff,
        organization_id: organization?.id,
        hourly_rate: parseFloat(newStaff.hourly_rate) || 0,
      });
      toast.success('Staff member added successfully');
      setIsDialogOpen(false);
      setNewStaff({
        user_id: '',
        employee_id: '',
        job_title: '',
        department: '',
        start_date: '',
        contract_type: 'permanent',
        hourly_rate: '',
        dbs_check_date: '',
        dbs_certificate_number: '',
        right_to_work_verified: false,
        right_to_work_expiry: '',
        visa_type: '',
      });
      fetchData();
    } catch (error) {
      console.error('Error creating staff:', error);
      toast.error('Failed to add staff member');
    }
  };

  const filteredStaff = staff.filter(member => {
    const user = member.user || {};
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) ||
           member.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           member.job_title?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getComplianceStatus = (member) => {
    const issues = [];
    if (!member.dbs_check_date) issues.push('DBS');
    if (!member.right_to_work_verified) issues.push('RTW');
    
    if (issues.length === 0) {
      return { status: 'compliant', label: 'Compliant', color: 'bg-green-100 text-green-700' };
    }
    return { status: 'issues', label: `Missing: ${issues.join(', ')}`, color: 'bg-amber-100 text-amber-700' };
  };

  return (
    <div className="min-h-screen" data-testid="staff-page">
      <Header title="Staff Management" />
      
      <div className="p-6 space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="relative flex-1 sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search staff..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="staff-search"
            />
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-staff-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Staff
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Staff Member</DialogTitle>
                <DialogDescription>
                  Enter staff details and compliance information
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateStaff} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="user_id">Select User *</Label>
                  <Select 
                    value={newStaff.user_id} 
                    onValueChange={(value) => setNewStaff(prev => ({ ...prev, user_id: value }))}
                  >
                    <SelectTrigger data-testid="staff-user-select">
                      <SelectValue placeholder="Select a user" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="employee_id">Employee ID</Label>
                    <Input
                      id="employee_id"
                      value={newStaff.employee_id}
                      onChange={(e) => setNewStaff(prev => ({ ...prev, employee_id: e.target.value }))}
                      placeholder="EMP001"
                      data-testid="staff-emp-id"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="job_title">Job Title *</Label>
                    <Input
                      id="job_title"
                      value={newStaff.job_title}
                      onChange={(e) => setNewStaff(prev => ({ ...prev, job_title: e.target.value }))}
                      required
                      placeholder="Care Worker"
                      data-testid="staff-job-title"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={newStaff.department}
                      onChange={(e) => setNewStaff(prev => ({ ...prev, department: e.target.value }))}
                      data-testid="staff-department"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={newStaff.start_date}
                      onChange={(e) => setNewStaff(prev => ({ ...prev, start_date: e.target.value }))}
                      required
                      data-testid="staff-start-date"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contract_type">Contract Type</Label>
                    <Select 
                      value={newStaff.contract_type} 
                      onValueChange={(value) => setNewStaff(prev => ({ ...prev, contract_type: value }))}
                    >
                      <SelectTrigger data-testid="staff-contract">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="permanent">Permanent</SelectItem>
                        <SelectItem value="fixed_term">Fixed Term</SelectItem>
                        <SelectItem value="zero_hours">Zero Hours</SelectItem>
                        <SelectItem value="agency">Agency</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hourly_rate">Hourly Rate (£)</Label>
                    <Input
                      id="hourly_rate"
                      type="number"
                      step="0.01"
                      value={newStaff.hourly_rate}
                      onChange={(e) => setNewStaff(prev => ({ ...prev, hourly_rate: e.target.value }))}
                      placeholder="12.50"
                      data-testid="staff-hourly-rate"
                    />
                  </div>
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Compliance Information
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="dbs_check_date">DBS Check Date</Label>
                      <Input
                        id="dbs_check_date"
                        type="date"
                        value={newStaff.dbs_check_date}
                        onChange={(e) => setNewStaff(prev => ({ ...prev, dbs_check_date: e.target.value }))}
                        data-testid="staff-dbs-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dbs_certificate_number">DBS Certificate Number</Label>
                      <Input
                        id="dbs_certificate_number"
                        value={newStaff.dbs_certificate_number}
                        onChange={(e) => setNewStaff(prev => ({ ...prev, dbs_certificate_number: e.target.value }))}
                        data-testid="staff-dbs-number"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="right_to_work_expiry">Right to Work Expiry</Label>
                      <Input
                        id="right_to_work_expiry"
                        type="date"
                        value={newStaff.right_to_work_expiry}
                        onChange={(e) => setNewStaff(prev => ({ ...prev, right_to_work_expiry: e.target.value }))}
                        data-testid="staff-rtw-expiry"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="visa_type">Visa Type (if applicable)</Label>
                      <Input
                        id="visa_type"
                        value={newStaff.visa_type}
                        onChange={(e) => setNewStaff(prev => ({ ...prev, visa_type: e.target.value }))}
                        placeholder="e.g., Skilled Worker"
                        data-testid="staff-visa"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="submit-staff">
                    Add Staff Member
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Staff</p>
                  <p className="text-3xl font-bold">{staff.length}</p>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <UserCog className="w-6 h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Compliant</p>
                  <p className="text-3xl font-bold text-green-600">
                    {staff.filter(s => s.dbs_check_date && s.right_to_work_verified).length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Issues</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {staff.filter(s => !s.dbs_check_date || !s.right_to_work_verified).length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Permanent</p>
                  <p className="text-3xl font-bold">
                    {staff.filter(s => s.contract_type === 'permanent').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Staff Table */}
        <Card data-testid="staff-table-card">
          <CardHeader>
            <CardTitle>All Staff Members</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 bg-slate-100 animate-pulse rounded" />
                ))}
              </div>
            ) : filteredStaff.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff Member</TableHead>
                    <TableHead>Employee ID</TableHead>
                    <TableHead>Job Title</TableHead>
                    <TableHead>Contract</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Compliance</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStaff.map((member) => {
                    const compliance = getComplianceStatus(member);
                    const user = member.user || {};
                    return (
                      <TableRow key={member.id} className="cursor-pointer hover:bg-slate-50" data-testid={`staff-row-${member.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-purple-700">
                                {user.first_name?.[0]}{user.last_name?.[0]}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium">{user.first_name} {user.last_name}</p>
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {user.email}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{member.employee_id || '-'}</TableCell>
                        <TableCell>{member.job_title}</TableCell>
                        <TableCell className="capitalize">{member.contract_type?.replace('_', ' ')}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            {member.start_date}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${compliance.color}`}>
                            {compliance.label}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={member.status === 'active' ? 'default' : 'secondary'}>
                            {member.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <UserCog className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No staff members found</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setIsDialogOpen(true)}
                >
                  Add Your First Staff Member
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
